import { graphHooks } from '@tunasong/graph-lib/react'
import type { ManyOfManyPoll, ManyOfManyPollResponse, Persisted, PollResponse } from '@tunasong/schemas'
import { isDateSlot } from '@tunasong/schemas'
import { Button, Checkbox, ConfirmDialog, FormControlLabel, Typography } from '@tunasong/ui-lib'
import type { ChangeEvent, FC } from 'react'
import { useState } from 'react'
import { getSlotTally, rankDateSlots } from './availability.js'
import { formatSlotFullLabel, formatSlotLabel } from './date-slot.js'
import styles from './date-poll-final.module.css'

export interface DatePollFinalListProps {
  poll: Persisted<ManyOfManyPoll>
  responses: Persisted<PollResponse>[]
  onChange(poll: Persisted<ManyOfManyPoll>): void
  onConfirm(): void
}

const countPicked = (finalOption?: Record<string, boolean>) => Object.values(finalOption ?? {}).filter(Boolean).length

/**
 * The owner's side — the only part of the feature that varies with `datesNeeded`.
 *
 * `finalOption` is already `Record<string, boolean>` on many-of-many, so multi-winner needs
 * no new persistence: only a counter, a ranking that honours "maybe ranks, it does not
 * count", and a confirm when the owner picks fewer than they said they needed.
 */
export const DatePollFinalList: FC<DatePollFinalListProps> = props => {
  const { poll, responses, onChange, onConfirm } = props

  const [showShortfallConfirm, setShowShortfallConfirm] = useState(false)

  const needed = poll.datesNeeded ?? 1
  const ranked = rankDateSlots(poll, responses)
  const picked = countPicked(poll.finalOption)
  const suggested = new Set(ranked.slice(0, needed))

  const handleToggle = (idx: number) => (ev: ChangeEvent<HTMLInputElement>) =>
    onChange({ ...poll, finalOption: { ...poll.finalOption, [idx]: ev.target.checked } })

  const handlePick = () => {
    if (picked < needed) {
      setShowShortfallConfirm(true)
      return
    }
    onConfirm()
  }

  const handleConfirmShortfall = () => {
    setShowShortfallConfirm(false)
    onConfirm()
  }

  return (
    <div className={styles.container}>
      <Typography variant="subtitle1">{needed === 1 ? 'Best so far' : `Top ${needed} so far`}</Typography>

      {ranked.map(idx => {
        const slot = poll.pollOptions[idx]
        if (!isDateSlot(slot)) {
          return null
        }
        const tally = getSlotTally(responses, idx)

        return (
          <FormControlLabel
            key={idx}
            data-slot-index={String(idx)}
            data-slot-suggested={suggested.has(idx) ? 'true' : 'false'}
            className={suggested.has(idx) ? styles.suggested : styles.row}
            control={
              <Checkbox value={String(idx)} checked={Boolean(poll.finalOption?.[idx])} onChange={handleToggle(idx)} />
            }
            label={
              <span className={styles.label} title={formatSlotFullLabel(slot)}>
                <span>{formatSlotLabel(slot.start)}</span>
                <span className={styles.tally}>{`${tally.yes} yes`}</span>
                {tally.maybe > 0 ? <span className={styles.maybe}>{`${tally.maybe} maybe`}</span> : null}
              </span>
            }
          />
        )
      })}

      {/*
        Live so the count is announced as it changes, not just seen.

        Above the target the progress framing stops being true — `2 of 1 picked` reads as
        impossible. Over-picking is legitimate ("we needed 3, but 4 nights work") and is not
        blocked, so the copy switches to stating both numbers instead, and the switch is
        itself the announcement that you have gone past what you asked for.
      */}
      <div className={styles.counter} role="status" aria-live="polite">
        {picked > needed ? `${picked} picked, ${needed} needed` : `${picked} of ${needed} picked`}
      </div>

      <Button disabled={picked === 0} onClick={handlePick}>
        Pick these dates
      </Button>

      <ConfirmDialog
        open={showShortfallConfirm}
        title="Fewer dates than you needed"
        text={`Pick ${picked} of the ${needed} dates you needed?`}
        okText="Pick these dates"
        onCancel={() => setShowShortfallConfirm(false)}
        onConfirm={handleConfirmShortfall}
      />
    </div>
  )
}

export interface DatePollFinalProps {
  poll: Persisted<ManyOfManyPoll>
  onChange(poll: Persisted<ManyOfManyPoll>): void
  onConfirm(): void
}

export const DatePollFinal: FC<DatePollFinalProps> = props => {
  const { poll, onChange, onConfirm } = props

  const { entities: responses = [] } = graphHooks.useEntityChildren<ManyOfManyPollResponse>({
    parentId: poll.id,
    filter: 'pollresponse',
  })

  return <DatePollFinalList poll={poll} responses={responses} onChange={onChange} onConfirm={onConfirm} />
}

export default DatePollFinal
